import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { useARC } from "providers/ARC/ARCProvider";
import { useWebRTC } from "providers/WebRTCProvider";
import { ArcsContext } from "providers/FleetProvider";
import LandmarksProvider from "providers/navigation/LandmarksProvider";
import ControlDebugProvider from "providers/debug/ControlDebugProvider";
import { AccessibilityPreferencesProvider } from "providers/AccessibilityPreferencesProvider";

import AnimatedModal from "components/layout/motion/AnimatedModal";
import SelectARC from "components/ARC/SelectARC";
import TopBar from "components/control/TopBar";
import DevNavLogsPopup from "components/control/DevNavLogsPopup";
import DevWebrtcPopup from "components/control/DevWebrtcPopup";
import InspectionPopup from "components/inspection/InspectionPopup";

export const ControlLayoutContext = createContext();
ControlLayoutContext.displayName = "ControlLayout";
export const useScreen = () => useContext(ControlLayoutContext);

const ControlLayout = () => {
    const { t } = useTranslation("control");
    const { selectedARCSerial, selectARC, selectedARC } = useARC();

    const { setWebRTCServerHost, signalingError, dismissSignalingError } = useWebRTC();

    // Optional: Only available in fleet mode, not in ARC mode
    const fleetContext = useContext(ArcsContext);
    const arcsInfo = useMemo(() => fleetContext?.arcsInfo || [], [fleetContext?.arcsInfo]);
    const arcStatuses = useMemo(() => fleetContext?.arcStatuses || {}, [fleetContext?.arcStatuses]);
    const arcStatus = arcStatuses[selectedARC?.serialNumber];

    const [showSelectARC, setShowSelectARC] = useState(false);
    const [screen, setScreen] = useState("stream"); // "stream" or "map"
    const [showSecondaryView, setShowSecondaryView] = useState(true);
    const [showStairsDialog, setShowStairsDialog] = useState(false);

    useEffect(() => {
        setShowSelectARC(!selectedARCSerial);
        setWebRTCServerHost(selectedARC?.host || null);
    }, [selectedARC, setShowSelectARC, selectedARCSerial, setWebRTCServerHost]);

    return (
        <ControlLayoutContext.Provider value={{ screen, setScreen, showSecondaryView, setShowSecondaryView, showStairsDialog, setShowStairsDialog }}>
            <AccessibilityPreferencesProvider>
                <ControlDebugProvider>
                    <LandmarksProvider>
                        <div className="layout flex min-h-0 flex-1 flex-col justify-between">
                            <TopBar arc={selectedARC} arcStatus={arcStatus} />
                            {signalingError && (
                                <div role="alert" className="flex items-center justify-between gap-3 bg-warning px-4 py-2 text-sm text-white">
                                    <span>{signalingError}</span>
                                    <button type="button" className="underline" onClick={dismissSignalingError}>
                                        {t("common:dismiss", { defaultValue: "Dismiss" })}
                                    </button>
                                </div>
                            )}
                            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                                <Outlet />
                            </div>
                            <AnimatedModal
                                visible={showSelectARC}
                                onClose={() => setShowSelectARC(false)}
                                className="w-150 max-w-full !overflow-visible"
                            >
                                <SelectARC
                                    arcsInfo={arcsInfo}
                                    arcStatuses={arcStatuses}
                                    cardOnClickTitle={t("layout.control")}
                                    cardOnClick={(info) => {
                                        selectARC(info.serialNumber);
                                    }}
                                    onClose={() => setShowSelectARC(false)}
                                />
                            </AnimatedModal>
                            <InspectionPopup />
                            <DevNavLogsPopup />
                            <DevWebrtcPopup />
                        </div>
                    </LandmarksProvider>
                </ControlDebugProvider>
            </AccessibilityPreferencesProvider>
        </ControlLayoutContext.Provider>
    );
};

export default ControlLayout;
